import { ActionIcon, Container, Flex, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconBoxMultiple, IconPlus, IconRefresh, IconShoppingCart } from '@tabler/icons-react'
import Big from 'big.js'
import dayjs from 'dayjs'
import { useGroupedShortages } from 'hooks/shortage'
import { MantineReactTable, MRT_ColumnDef, MRT_Row, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { Account } from 'types/auth'
import { Category } from 'types/products'
import { GroupedShortageTableItem, ShortageTableItem } from 'types/shortage'
import { categoriesForSelect, getCategoryName, getUnitName } from 'utils'

type Props = {
  users?: Array<Account>
  ungroup: () => void
}

export default function GroupedShortageTable({ users, ungroup }: Props) {
  function openAddShortageModal() {
    modals.openContextModal({
      modal: 'addShortage',
      centered: true,
      title: 'Dodaj brak produktu',
      innerProps: {},
    })
  }

  function openOrderShortagesModal(groupedShortage: GroupedShortageTableItem) {
    modals.openContextModal({
      modal: 'orderShortages',
      centered: true,
      title: 'Zamów braki produktów',
      size: 'xl',
      innerProps: {
        groupedShortage,
      },
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<GroupedShortageTableItem>>>(
    () => [
      {
        accessorKey: 'product.symbol',
        header: 'Symbol produktu',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po symbolu',
        },
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          const symbol = cell.getValue<string>()
          return isSubRow ? (
            ''
          ) : (
            <span>
              {symbol} <b>({subRows?.length})</b>
            </span>
          )
        },
      },
      {
        accessorKey: 'size',
        header: 'Wymiar',
        size: 160,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po wymiarze',
        },
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          const size = cell.getValue<string>()
          return isSubRow ? '' : `${size} cm`
        },
      },
      {
        accessorKey: 'category',
        header: 'Kategoria',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po kategorii',
          data: categoriesForSelect,
        },
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          const category = cell.getValue<Category>()
          const categoryName = getCategoryName(category, { capitalize: true }).mianownik
          return isSubRow ? '' : categoryName
        },
      },
      {
        accessorKey: 'feature',
        header: 'Cecha',
        size: 150,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po cesze',
        },
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          const feature = cell.getValue<string>()
          return isSubRow ? '' : feature
        },
      },
      {
        accessorKey: 'amount',
        header: 'Ilość',
        size: 120,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po ilości',
        },
        enableColumnFilter: false,
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          if (!isSubRow) {
            // get the sum of amounts from subRows
            const rows = (subRows ?? []) as unknown as Array<MRT_Row<ShortageTableItem>>
            const totalAmount = rows.reduce((sum, row) => sum.plus(row.original.amount || 0), new Big(0)).toNumber()

            const unit = rows[0]?.original.unit
            return (
              <Flex direction="column">
                <span>Łączna ilość</span>
                <span>
                  <b>
                    {totalAmount} {getUnitName(unit).short}
                  </b>
                </span>
              </Flex>
            )
          }
          const amount = cell.getValue<string>()
          const unit = (cell.row.original as unknown as ShortageTableItem).unit
          if (!amount) return ''
          return `${amount} ${getUnitName(unit).short}`
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Data dodania',
        filterVariant: 'date',
        enableColumnFilter: false,
        Cell: ({ cell, row: { subRows } }) => {
          const isSubRow = subRows?.length === 0
          if (!isSubRow) {
            // get the earliest createdAt from subRows
            const dates = subRows ?? []
            const latestDate = Math.max(
              ...dates.map((subRow) => dayjs((subRow.original as unknown as ShortageTableItem).createdAt).valueOf())
            )
            return (
              <Flex direction="column">
                <span>Najnowszy brak</span>
                <span>
                  <b>{dayjs(latestDate).format('D MMMM HH:mm')}</b>
                </span>
              </Flex>
            )
          }
          const createdAt = cell.getValue<Date>()
          if (!createdAt) return ''
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie dodania',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
        size: 200,
      },
      {
        accessorKey: 'orderNumber',
        header: 'Numer zamówienia',
        size: 230,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po numerze zamówienia',
        },
        enableColumnFilter: false,
      },
      ...(users
        ? [
            {
              accessorKey: 'createdById',
              header: 'Dodane przez',
              filterVariant: 'select',
              enableColumnFilter: false,
              mantineFilterSelectProps: {
                placeholder: 'Filtruj po użytkowniku',
                data: users.map((user) => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                })),
              },
              Cell: ({ cell }) => {
                const createdById = cell.getValue<string>()
                if (!createdById) return ''
                const user = users.find((user) => user.id === createdById)
                return `${user?.firstName} ${user?.lastName}`
              },
            } as MRT_ColumnDef<GroupedShortageTableItem>,
          ]
        : []),
      {
        accessorKey: 'comments',
        header: 'Uwagi',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po uwagach',
        },
        enableColumnFilter: false,
      },
    ],
    []
  )
  const groupedShortages = useGroupedShortages()

  const memoizedGroupedData = useMemo(() => groupedShortages.data ?? [], [JSON.stringify(groupedShortages.data)])
  const table = useMantineReactTable({
    columns,
    data: memoizedGroupedData,
    layoutMode: 'grid',
    state: {
      isLoading: groupedShortages.isLoading,
      showAlertBanner: groupedShortages.isError,
      showProgressBars: groupedShortages.isFetching,
      showColumnFilters: true,
    },
    initialState: {
      showGlobalFilter: false,
      showColumnFilters: true,
      columnPinning: { right: ['mrt-row-actions'] },
    },
    enableExpanding: true,
    enableGlobalFilterModes: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    enableColumnPinning: true,
    manualFiltering: false,
    manualPagination: false,
    paginateExpandedRows: false,
    autoResetPageIndex: false,
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        {!!users && (
          <Tooltip label="Wyłącz grupowanie">
            <ActionIcon size="lg" variant="subtle" onClick={ungroup} ml="auto" mr={4}>
              <IconBoxMultiple />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Dodaj brak produktu">
          <ActionIcon size="lg" variant="subtle" onClick={() => openAddShortageModal()} ml="auto" mr={4}>
            <IconPlus />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odśwież dane">
          <ActionIcon
            size="lg"
            variant="subtle"
            color="gray"
            onClick={() => {
              groupedShortages.refetch()
            }}
          >
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    mantineToolbarAlertBannerProps: groupedShortages.isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${groupedShortages.error})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10', '15'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
        enablePinning: true,
      },
    },
    renderRowActions: ({ row }) => {
      const isSubRow = row.subRows?.length === 0
      if (!isSubRow)
        return (
          <Tooltip label="Zamów braki">
            <ActionIcon
              variant="light"
              radius="xl"
              mx="lg"
              onClick={() => openOrderShortagesModal(row.original as unknown as GroupedShortageTableItem)}
            >
              <IconShoppingCart size={18} />
            </ActionIcon>
          </Tooltip>
        )
    },
  })

  return <MantineReactTable table={table} />
}
